import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Eye, Trash2, Mail, Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { computeLayout } from '../configurator/layout';
import { normalizeConfig } from '../configurator/catalog';
import { makeCtx } from '../configurator/parts';
import { ViewCanvas, VIEWS } from '../configurator/ViewCanvas';
import { Thread } from '../configurator/Thread';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ['nouveau', 'en étude', 'devis envoyé', 'archivé'];

function ConfigViewer({ conf, onClose, onRead }) {
  const [mode, setMode] = useState('perspective');
  const [wall, setWall] = useState('A');
  const valid = Array.isArray(conf.config?.modules) && conf.config?.mats && conf.config?.room;
  const cfgN = useMemo(() => (valid ? normalizeConfig(conf.config) : null), [conf, valid]);
  const L = useMemo(() => (cfgN ? computeLayout(cfgN) : null), [cfgN]);
  const c = useMemo(() => makeCtx('ad', cfgN?.mats || {}, cfgN?.led, false), [cfgN]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto bg-[#151515] border-[#a68966]/30 text-gray-200" data-testid="config-viewer-dialog">
        <DialogHeader><DialogTitle className="font-playfair text-white">{conf.category_label} · {conf.reference} · {conf.name}</DialogTitle></DialogHeader>
        <div className="flex gap-1 mb-2">
          {Object.entries(VIEWS).map(([k, v]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`admin-view-tab-${k}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest ${mode === k ? 'bg-[#a68966] text-[#1a1a1a]' : 'text-gray-400 border border-white/10'}`}>{v.label}</button>
          ))}
        </div>
        {mode === 'elevation' && L?.walls.length > 1 && (
          <div className="flex gap-1">{L.walls.map((w) => <button key={w} type="button" onClick={() => setWall(w)} data-testid={`admin-wall-${w}`} className={`px-3 py-1 text-xs ${wall === w ? 'text-[#4dffb8] border border-[#4dffb8]/50' : 'text-gray-500 border border-white/10'}`}>Mur {w}</button>)}</div>
        )}
        <div className="bg-[#f6f1e8] h-[34vh] font-montserrat" key={`${mode}${wall}`}>{L ? <ViewCanvas kind={mode} L={L} c={c} sel={null} onPick={() => {}} p={`ad${mode}`} wall={wall} /> : <p className="p-8 text-gray-600">Configuration illisible.</p>}</div>
        <div className="grid md:grid-cols-2 gap-4 text-xs text-gray-400 mt-2 font-montserrat">
          <ul className="space-y-1">{conf.summary.map((s) => <li key={s}>{s}</li>)}</ul>
          <ul className="space-y-1">{conf.materials.map((s) => <li key={s}>{s}</li>)}</ul>
        </div>
        <div className="font-montserrat">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#a68966] mb-2">Discussion avec {conf.name}</p>
          <Thread path={`/configurations/${conf.id}/messages`} me="jenma" onRead={onRead} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const ConfigurationsManager = () => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/configurations`, { credentials: 'include' });
    setItems(r.ok ? await r.json() : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    const r = await fetch(`${API_URL}/api/configurations/${id}/status`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
    if (r.ok) { setItems((a) => a.map((x) => (x.id === id ? { ...x, status } : x))); toast.success('Statut mis à jour'); } else toast.error('Erreur de mise à jour');
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cette configuration ?')) return;
    const r = await fetch(`${API_URL}/api/configurations/${id}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) setItems((a) => a.filter((x) => x.id !== id));
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#a68966]" /></div>;
  return (
    <div className="space-y-4" data-testid="configurations-manager">
      <h2 className="text-2xl font-playfair text-white">Configurations clients <span className="text-[#a68966] text-lg">({items.length})</span></h2>
      {items.length === 0 && <p className="text-gray-500 py-10 text-center">Aucune configuration reçue pour le moment.</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it) => (
          <div key={it.id} className="bg-[#1a1a1a] border border-[#a68966]/20 p-5 space-y-3" data-testid={`configuration-card-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#a68966]">{it.category_label}</p>
                <p className="text-white font-playfair text-lg flex items-center gap-2">{it.name}{it.unread_admin && <span className="text-[9px] uppercase tracking-widest bg-[#4dffb8] text-[#1a1a1a] px-2 py-0.5" data-testid={`configuration-unread-${it.id}`}>Nouveau message</span>}</p>
                <p className="text-xs text-gray-500">{it.reference} · {new Date(it.timestamp).toLocaleString('fr-FR')}</p>
              </div>
              <select value={it.status} onChange={(e) => setStatus(it.id, e.target.value)} data-testid={`configuration-status-${it.id}`}
                className="bg-[#111] border border-white/10 text-xs text-gray-200 px-2 py-1">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="text-xs text-gray-400 space-y-1">
              <a href={`mailto:${it.email}`} className="flex items-center gap-2 hover:text-[#a68966]"><Mail className="w-3 h-3" />{it.email}</a>
              {it.phone && <a href={`tel:${it.phone}`} className="flex items-center gap-2 hover:text-[#a68966]"><Phone className="w-3 h-3" />{it.phone}</a>}
            </div>
            {it.message && <p className="text-xs text-gray-300 border-l-2 border-[#a68966]/50 pl-3 line-clamp-3">{it.message}</p>}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setOpen(it)} data-testid={`configuration-view-${it.id}`} className="flex-1 flex items-center justify-center gap-2 bg-[#a68966] text-[#1a1a1a] text-xs font-semibold py-2 hover:bg-[#b89c78]"><Eye className="w-4 h-4" />Plans & discussion</button>
              <button type="button" onClick={() => remove(it.id)} data-testid={`configuration-delete-${it.id}`} className="px-3 border border-white/10 text-gray-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {open && <ConfigViewer conf={open} onClose={() => setOpen(null)} onRead={() => setItems((a) => a.map((x) => (x.id === open.id ? { ...x, unread_admin: false } : x)))} />}
    </div>
  );
};
